import { Injectable } from '@angular/core';
import { DefaultUrlSerializer, UrlTree } from '@angular/router';
import { SiteContextParamsService } from './site-context-params.service';

/**
 * Values of the site context parameters encoded in the URL.
 */
export interface SiteContextUrlParams {
  [name: string]: string;
}

/**
 * Angular URL Serializer aware of Spartacus site context parameters
 * encoded in the URL.
 */
@Injectable()
export class SiteContextUrlSerializer extends DefaultUrlSerializer {
  /**
   * Splits the URL into 2 parts: path and the query/fragment part
   */
  protected readonly URL_SPLIT: RegExp = /(^[^#?]*)(.*)/;

  /**
   * Names of site context parameters encoded in the URL
   */
  protected get urlEncodingParameters(): string[] {
    return this.siteContextParams.getUrlEncodingParameters();
  }

  /**
   * Tells whether any site context parameters should be encoded in the URL
   */
  protected get hasContextInRoutes(): boolean {
    return this.urlEncodingParameters.length > 0;
  }

  constructor(private siteContextParams: SiteContextParamsService) {
    super();
  }

  /**
   * @override Recognizes the site context parameters encoded in the prefix segments
   * of the given URL.
   *
   * It returns the UrlTree for the given URL shortened by the recognized params, but saves
   * the params' values in the custom property of UrlTree: `siteContext`.
   */
  parse(url: string): UrlTree {
    // экранирование круглых скобок в урле
    url = url.replace(/\(/g, '%28').replace(/\)/g, '%29');

    if (this.hasContextInRoutes) {
      const urlWithParams = this.urlExtractContextParameters(url);
      const parsed = super.parse(urlWithParams.url);
      return parsed;
    } else {
      return super.parse(url);
    }
  }

  /**
   * Recognizes the site context parameters encoded in the prefix segments of the given URL.
   *
   * It returns the recognized site context params as well as the
   * URL shortened by the recognized params.
   */
  urlExtractContextParameters(url: string): {
    url: string;
    params: SiteContextUrlParams;
  } {
    const [, urlPart, queryPart] = url.match(this.URL_SPLIT);

    const segments = urlPart.split('/');
    if (segments[0] === '') {
      segments.shift();
    }
    const params = {};

    let paramId = 0;
    let segmentId = 0;
    while (paramId < this.urlEncodingParameters.length && segmentId < segments.length) {
      const paramName = this.urlEncodingParameters[paramId];
      const paramValues = this.siteContextParams.getParamValues(paramName);

      if (paramValues.includes(segments[segmentId])) {
        params[paramName] = segments[segmentId];
        segmentId++;
      }
      paramId++;
    }

    url = segments.slice(segmentId).join('/') + queryPart;
    return { url, params };
  }

  /**
   * @override Serializes the given UrlTree to a string and prepends
   *  to it the current values of the site context parameters.
   */
  serialize(tree: UrlTree): string {
    let url = super.serialize(tree);

    // предотвращаем добавление завершающего слеша в урл главной страницы
    if (url.split('?')[0] === '/') {
      url = url.slice(1);
    }

    const serialized = this.urlIncludeContextParameters(url);
    return serialized;
  }

  /**
   * Prepends the current values of the site context parameters to the given URL.
   */
  protected urlIncludeContextParameters(url: string): string {
    const contextRoutePart = this.urlEncodingParameters
      .map((param) => {
        return this.siteContextParams[param] ? this.siteContextParams[param] : this.siteContextParams.getValue(param);
      })
      .join('/');

    return contextRoutePart + url;
  }
}
